import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";

import useData from "../../hooks/useData";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const VendorsCategoriesPanel = ({ onRefresh }) => {
  const { getFromServer, executeAction } = useData();

  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openVendorForm, setOpenVendorForm] = useState(false);
  const [openCategoryForm, setOpenCategoryForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  const [vendorForm, setVendorForm] = useState({
    name: "",
    contactInfo: "",
    isActive: true,
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    taxExempt: false,
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const vendorData = await getFromServer({
      entityName: "Vendor",
      filter: { isActive: true },
    });
    setVendors(vendorData || []);

    const categoryData = await getFromServer({
      entityName: "VendorInvoiceCategory",
      filter: { isActive: true },
    });
    setCategories(categoryData || []);
  };

  const handleSaveVendor = async () => {
    if (!vendorForm.name) {
      alert("Vendor name required");
      return;
    }

    const results = await executeAction({
      entityName: "Vendor",
      action: "add",
      data: vendorForm,
    });

    if (results.isSuccessful) {
      setOpenVendorForm(false);
      setVendorForm({ name: "", contactInfo: "", isActive: true });
      await loadData();
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) {
      alert("Category name required");
      return;
    }

    const results = await executeAction({
      entityName: "VendorInvoiceCategory",
      action: "add",
      data: categoryForm,
    });

    if (results.isSuccessful) {
      setOpenCategoryForm(false);
      setCategoryForm({ name: "", taxExempt: false, isActive: true });
      await loadData();
    }
  };

  const handleDeleteVendor = async () => {
    if (!deleteConfirmId) return;
    await executeAction({
      entityName: "Vendor",
      action: "delete",
      data: { id: deleteConfirmId },
    });
    await loadData();
    setDeleteConfirmId(null);
  };

  const handleDeleteCategory = async () => {
    if (!deleteConfirmId) return;
    await executeAction({
      entityName: "VendorInvoiceCategory",
      action: "delete",
      data: { id: deleteConfirmId },
    });
    await loadData();
    setDeleteConfirmId(null);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
    >
      {/* Vendors Section */}
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
        >
          <Typography variant="h6">Vendors</Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenVendorForm(true)}
          >
            Add Vendor
          </Button>
        </Box>

        {vendors.length > 0 ? (
          <Card>
            <CardContent>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact Info</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell fontWeight="bold">{vendor.name}</TableCell>
                      <TableCell>{vendor.contactInfo || "-"}</TableCell>
                      <TableCell>
                        {vendor.isActive ? "Active" : "Inactive"}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteType("vendor");
                            setDeleteConfirmId(vendor.id);
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <Typography color="textSecondary">No vendors found</Typography>
        )}
      </Box>

      {/* Categories Section */}
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
        >
          <Typography variant="h6">Vendor Invoice Categories</Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenCategoryForm(true)}
          >
            Add Category
          </Button>
        </Box>

        {categories.length > 0 ? (
          <Card>
            <CardContent>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Tax Exempt</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell fontWeight="bold">{category.name}</TableCell>
                      <TableCell>{category.taxExempt ? "✓" : "-"}</TableCell>
                      <TableCell>
                        {category.isActive ? "Active" : "Inactive"}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteType("category");
                            setDeleteConfirmId(category.id);
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <Typography color="textSecondary">No categories found</Typography>
        )}
      </Box>

      {/* Add Vendor Dialog */}
      <Dialog
        open={openVendorForm}
        onClose={() => setOpenVendorForm(false)}
      >
        <DialogTitle>Add Vendor</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 2,
          }}
        >
          <TextField
            label="Vendor Name"
            value={vendorForm.name}
            onChange={(e) =>
              setVendorForm({ ...vendorForm, name: e.target.value })
            }
            fullWidth
            required
          />
          <TextField
            label="Contact Info"
            value={vendorForm.contactInfo}
            onChange={(e) =>
              setVendorForm({ ...vendorForm, contactInfo: e.target.value })
            }
            fullWidth
            multiline
            rows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={vendorForm.isActive}
                onChange={(e) =>
                  setVendorForm({ ...vendorForm, isActive: e.target.checked })
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVendorForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveVendor}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog
        open={openCategoryForm}
        onClose={() => setOpenCategoryForm(false)}
      >
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 2,
          }}
        >
          <TextField
            label="Category Name"
            value={categoryForm.name}
            onChange={(e) =>
              setCategoryForm({ ...categoryForm, name: e.target.value })
            }
            fullWidth
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={categoryForm.taxExempt}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    taxExempt: e.target.checked,
                  })
                }
              />
            }
            label="Tax Exempt"
          />
          <FormControlLabel
            control={
              <Switch
                checked={categoryForm.isActive}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    isActive: e.target.checked,
                  })
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog_YesNo
        open={Boolean(deleteConfirmId)}
        title={`Delete ${deleteType === "vendor" ? "Vendor" : "Category"}?`}
        message={`Are you sure you want to delete this ${
          deleteType === "vendor" ? "vendor" : "category"
        }?`}
        onYes={
          deleteType === "vendor" ? handleDeleteVendor : handleDeleteCategory
        }
        onNo={() => setDeleteConfirmId(null)}
      />
    </Box>
  );
};

export default VendorsCategoriesPanel;
